import { AnalyticsEvents } from '@mondaygpt/analytics-events'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAnalytics } from 'hooks/use-analytics'
import {
  FallbackApi,
  type FallbackConnectSession,
  type FallbackPageSummary,
  type FallbackPlatform,
  type FallbackProviderId,
} from '../api/fallback-api'
import { finishOAuthPickerStep } from '../utils/oauth-popup-complete'
import { classifyClientConnectError } from '../utils/connect-failure-class'
import { errorReportingService } from 'services/error-reporting/error-reporting.service'

// Survives a redirect-mode (Composio) round-trip so we can resume listing pages
// when the user lands back on the picker.
const PENDING_KEY = 'taka_fallback_pending'

type Phase = 'idle' | 'connecting' | 'listing' | 'selecting'

interface PendingConnect {
  provider: FallbackProviderId
  externalUserId: string
  platform: FallbackPlatform
}

/**
 * Drives the connect-via-partner (fallback) flow for one platform, so any
 * platform's empty-state picker can drop in a single button. Handles both
 * partner connect modes:
 *   - `sdk`    (Phyllo): launch the web SDK inline, then list linked pages.
 *   - `redirect` (Composio): navigate to the hosted consent, resume on return.
 * After the user picks a page it persists via the server and finishes the
 * OAuth picker step (closes the popup / navigates to integrations).
 */
export function useFallbackConnect(opts: { platform: FallbackPlatform; fromOAuth: boolean }) {
  const { platform, fromOAuth } = opts
  const navigate = useNavigate()
  const { track } = useAnalytics()

  const [phase, setPhase] = useState<Phase>('idle')
  const [pages, setPages] = useState<FallbackPageSummary[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const pending = useRef<PendingConnect | null>(null)

  const trackFailed = useCallback(
    (step: string, err: unknown, provider?: FallbackProviderId) => {
      const message = err instanceof Error ? err.message : String(err)
      const failureClass = classifyClientConnectError(err)
      // Only real errors reach error tracking/console — a user cancel (e.g. closing the
      // Phyllo SDK → "Connection cancelled") must not page us. The analytics
      // events below still record the cancel (failure_class=user_aborted).
      if (failureClass !== 'user_aborted') {
        console.error(`[fallback-connect] ${step} failed:`, err)
        errorReportingService.captureException(err instanceof Error ? err : new Error(message), {
          feature: 'social_connect',
          step,
          social_gateway: provider ?? 'unknown',
          platform,
          failure_class: failureClass,
        })
      }
      track(AnalyticsEvents.Social.FALLBACK_CONNECT_FAILED, {
        platform,
        provider,
        error: message,
        step,
      })
      // Unified connect funnel (dual-emit during migration). `provider` is
      // undefined when connect-session fails before the gateway is resolved.
      track(AnalyticsEvents.Social.SOCIAL_CONNECT_FAILED, {
        platform,
        social_gateway: provider ?? 'unknown',
        error: message,
        step,
        failure_class: failureClass,
      })
      setError('We could not connect through our publishing partner. Please try again.')
      setPhase('idle')
    },
    [platform, track]
  )

  const loadPages = useCallback(
    async (ctx: PendingConnect) => {
      setPhase('listing')
      try {
        const res = await FallbackApi.listPages({
          provider: ctx.provider,
          externalUserId: ctx.externalUserId,
          platform: ctx.platform,
        })
        pending.current = ctx
        setPages(res.pages ?? [])
        setPhase('idle')
      } catch (err) {
        trackFailed('list_pages', err, ctx.provider)
      }
    },
    [trackFailed]
  )

  // Resume a redirect-mode connection after the browser returns to the picker.
  useEffect(() => {
    const raw = sessionStorage.getItem(PENDING_KEY)
    if (!raw) return
    sessionStorage.removeItem(PENDING_KEY)
    try {
      const ctx = JSON.parse(raw) as PendingConnect
      if (ctx.platform === platform) void loadPages(ctx)
    } catch {
      // ignore malformed marker
    }
  }, [platform, loadPages])

  const start = useCallback(async () => {
    setError(null)
    setPhase('connecting')
    let session: FallbackConnectSession
    try {
      session = await FallbackApi.connectSession({ platform })
    } catch (err) {
      trackFailed('connect_session', err)
      return
    }

    track(AnalyticsEvents.Social.FALLBACK_CONNECT_STARTED, {
      platform,
      provider: session.gateway,
    })
    // Unified connect funnel (dual-emit during migration). Single client-side
    // emitter for a partner-gateway connect start; the gateway is now resolved.
    track(AnalyticsEvents.Social.SOCIAL_CONNECT_INITIATED, {
      platform,
      social_gateway: session.gateway,
      source: 'integrations_page',
    })

    if (session.mode === 'redirect') {
      // Stash context so we can list pages when the user returns, then leave.
      sessionStorage.setItem(
        PENDING_KEY,
        JSON.stringify({
          provider: session.gateway,
          externalUserId: session.externalUserId,
          platform,
        } satisfies PendingConnect)
      )
      window.location.href = session.redirectUrl
      return
    }

    // sdk mode (Phyllo): launch the web SDK, then list linked pages.
    try {
      await launchPhylloConnect(session)
      await loadPages({
        provider: session.gateway,
        externalUserId: session.externalUserId,
        platform,
      })
    } catch (err) {
      trackFailed('sdk_link', err, session.gateway)
    }
  }, [platform, track, trackFailed, loadPages])

  const selectPage = useCallback(
    async (externalAccountId: string) => {
      const ctx = pending.current
      if (!ctx) return
      setPhase('selecting')
      try {
        await FallbackApi.selectPage({
          provider: ctx.provider,
          externalAccountId,
          externalUserId: ctx.externalUserId,
          platform,
        })
        finishOAuthPickerStep({ provider: platform, fromOAuth, navigate })
      } catch (err) {
        trackFailed('select_page', err, ctx.provider)
      }
    },
    [platform, fromOAuth, navigate, trackFailed]
  )

  return { phase, pages, error, start, selectPage }
}

/**
 * Load + open the Phyllo Connect web SDK and resolve once an account is linked.
 *
 * ⚠️ TO-VERIFY against Phyllo docs: the exact SDK CDN URL / npm package, the
 * global name, the init option keys, and the event names. The shape below
 * follows Phyllo's documented `PhylloConnect.initialize(...)` + event model.
 */
export function launchPhylloConnect(session: {
  sdkToken: string
  externalUserId: string
  environment: string
}): Promise<void> {
  return new Promise((resolve, reject) => {
    void loadPhylloConnectScript()
      .then(PhylloConnect => {
        const connect = PhylloConnect.initialize({
          clientDisplayName: 'Taka',
          environment: session.environment,
          userId: session.externalUserId,
          token: session.sdkToken,
        })
        connect.on('accountConnected', () => resolve())
        connect.on('connectionFailure', (reason: unknown) =>
          reject(new Error(`Phyllo connection failed: ${String(reason)}`))
        )
        connect.on('exit', () => reject(new Error('Connection cancelled')))
        connect.open()
      })
      .catch(reject)
  })
}

interface PhylloConnectGlobal {
  initialize(config: {
    clientDisplayName: string
    environment: string
    userId: string
    token: string
  }): {
    on(event: string, cb: (...args: unknown[]) => void): void
    open(): void
  }
}

const PHYLLO_CONNECT_SRC = 'https://cdn.getphyllo.com/connect/v2/phyllo-connect.js'

function loadPhylloConnectScript(): Promise<PhylloConnectGlobal> {
  const existing = (window as unknown as { PhylloConnect?: PhylloConnectGlobal }).PhylloConnect
  if (existing) return Promise.resolve(existing)

  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = PHYLLO_CONNECT_SRC
    script.async = true
    script.onload = () => {
      const global = (window as unknown as { PhylloConnect?: PhylloConnectGlobal }).PhylloConnect
      if (global) resolve(global)
      else reject(new Error('Phyllo Connect SDK loaded but global not found'))
    }
    script.onerror = () => reject(new Error('Failed to load Phyllo Connect SDK'))
    document.body.appendChild(script)
  })
}
